"use client";

import React, { useEffect, useMemo, useState } from "react";
import { useUser } from "@/lib/context/user-context";
import { shadowingTracker, DailyGoal } from "@/lib/shadowing-tracker";
import { useI18n } from "@/lib/i18n";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";

export function ShadowingGoalSection() {
  const { user } = useUser();
  const { t } = useI18n() as any;
  const userId = user?.id;

  const [currentGoal, setCurrentGoal] = useState<DailyGoal | null>(null);
  const [minutes, setMinutes] = useState<number>(10);
  // Sessions are not user-configurable now; keep a sensible default for API compatibility
  const DEFAULT_SESSIONS = 5;
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    async function load() {
      if (!userId) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError(null);
        const params = new URLSearchParams({ userId, type: "goal" });
        const res = await fetch(`/api/shadowing-stats?${params.toString()}`);
        if (!res.ok) throw new Error(t('settings.dailyGoal.loadFailed'));
        const goal = (await res.json()) as DailyGoal;
        if (!mounted) return;
        setCurrentGoal(goal);
        setMinutes(goal.targetMinutes || 10);
      } catch (e: any) {
        if (!mounted) return;
        setError(e?.message || t('settings.dailyGoal.loadFailed'));
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => {
      mounted = false;
    };
  }, [userId]);

  const percentage = useMemo(() => {
    if (!currentGoal) return 0;
    if (!currentGoal.targetMinutes) return 0;
    return Math.min(100, Math.round((currentGoal.completedMinutes / currentGoal.targetMinutes) * 100));
  }, [currentGoal]);

  const handleSave = async () => {
    if (!userId) return;
    try {
      setSaving(true);
      setSavedMsg(null);
      await shadowingTracker.setDailyGoal(DEFAULT_SESSIONS, minutes, userId);
      setSavedMsg(t('settings.dailyGoal.saved'));
      // refresh currentGoal
      const params = new URLSearchParams({ userId, type: "goal" });
      const res = await fetch(`/api/shadowing-stats?${params.toString()}`);
      if (res.ok) {
        const goal = (await res.json()) as DailyGoal;
        setCurrentGoal(goal);
      }
    } catch (e: any) {
      setError(e?.message || t('settings.dailyGoal.saveFailed'));
    } finally {
      setSaving(false);
      setTimeout(() => setSavedMsg(null), 2000);
    }
  };

  const minutesClamped = Math.max(1, Math.min(600, minutes));
  const remaining = Math.max(0, (currentGoal?.targetMinutes || minutesClamped) - (currentGoal?.completedMinutes || 0));

  return (
    <section className="mb-8">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-lg sm:text-xl font-semibold text-foreground">{t('settings.dailyGoal.title')}</h2>
          <p className="text-sm text-muted-foreground">{t('settings.dailyGoal.desc')}</p>
        </div>
        {currentGoal && (
          <div className="hidden sm:flex items-center gap-4 pr-1">
            {/* 小型环形进度 */}
            <GoalRing percent={percentage} size={44} />
            <div className="text-sm text-muted-foreground">
              <div>
                {t('settings.dailyGoal.completedPrefix')} {Math.round(currentGoal.completedMinutes)} {t('settings.dailyGoal.minAbbr')} {t('settings.dailyGoal.todayWord')}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-5">
        {loading ? (
          <div className="flex items-center gap-4">
            <div className="h-[10px] w-40 bg-muted-foreground/20 rounded animate-pulse" />
            <div className="h-[10px] w-24 bg-muted-foreground/20 rounded animate-pulse" />
          </div>
        ) : (
          <>
            {error && <div className="text-sm text-red-600">{error}</div>}

            <div className="w-full max-w-[800px] space-y-4">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-foreground">
                  {t('settings.dailyGoal.targetMinutes')}
                </label>
                <span className="text-sm font-medium text-muted-foreground w-12 text-right">
                  {minutesClamped}m
                </span>
              </div>
              
              <div className="flex items-center gap-4">
                <Slider
                  value={[Math.min(minutesClamped, 120)]}
                  min={1}
                  max={120}
                  step={1}
                  onValueChange={(vals) => setMinutes(vals[0])}
                  className="flex-1 cursor-pointer"
                />
                <Input
                type="number"
                min={1}
                max={600}
                  value={minutesClamped}
                  onChange={(e) => setMinutes(Math.max(1, Math.min(600, Number(e.target.value || 0))))}
                  className="w-20 text-center"
                />
              </div>
              {/* <p className="text-xs text-muted-foreground">
                {t('settings.dailyGoal.minutesLabel')}
              </p> */}
            </div>

            {/* Footer actions + progress info */}
            <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
              <div className="text-sm text-muted-foreground flex-1 min-w-[220px]">
                {currentGoal ? (
                  <div className="flex items-baseline gap-1">
                    <span>{t('settings.dailyGoal.progressPrefix')}</span>
                    <span className="font-medium text-foreground">{Math.round(currentGoal.completedMinutes)}</span>
                    <span>/</span>
                    <span>{currentGoal.targetMinutes} {t('settings.dailyGoal.minAbbr')}</span>
                  </div>
                ) : (
                  <span>{t('settings.dailyGoal.desc')}</span>
                )}
              </div>
              <div className="flex items-center gap-3 relative">
                {savedMsg && (
                  <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 text-sm text-green-600 font-medium animate-in fade-in slide-in-from-right-4 whitespace-nowrap">
                    {savedMsg}
                  </span>
                )}
                <Button
                  onClick={handleSave}
                  disabled={saving || !userId}
                  size="sm"
                  className="min-w-[80px]"
                >
                  {saving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('settings.dailyGoal.saving')}
                    </>
                  ) : (
                    t('common.save')
                  )}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

function GoalRing({ percent, size = 44 }: { percent: number; size?: number }) {
  const clamped = Math.max(0, Math.min(100, percent));
  const r = (size - 6) / 2; // padding for stroke
  const c = 2 * Math.PI * r;
  const dash = (clamped / 100) * c;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size / 2} cy={size / 2} r={r} stroke="#eee" strokeWidth="6" fill="none" />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        stroke="#ec4899"
        strokeWidth="6"
        fill="none"
        strokeLinecap="round"
        strokeDasharray={`${dash} ${c - dash}`}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
    </svg>
  );
}
